import SectionType from 'models/types/ts/sectionType'
import ContentType from 'models/types/ts/contentType'
import React from 'react'
import cx from 'classnames'
import ReactMarkdown from 'utils/markdown'
import { useInView } from 'react-intersection-observer'
import style from './PastEvents.module.scss'

interface Props {
    events: SectionType
}

interface EventProps {
    event: ContentType
    parentInView: boolean
    row: number
}

const Event = ({ event, parentInView, row }: EventProps): JSX.Element => {
    const [open, setOpen] = React.useState(false)
    const containerRef = React.useRef(null)
    const { ref, inView } = useInView({
        threshold: 0.3,
        triggerOnce: true,
    })

    React.useEffect(() => {
        const getHandleClick = () => (e: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(e.target as Node)
            ) {
                setOpen(false)
            }
        }

        const handleClick = getHandleClick()

        document.addEventListener('click', handleClick)

        return () => {
            document.removeEventListener('click', handleClick)
        }
    })

    return (
        <div
            ref={containerRef}
            style={{ transitionDelay: `${row * 0.6}s` }}
            className={cx(style.eventContainer, {
                [style.visible]: parentInView,
            })}
        >
            <div
                className={cx(style.eventContent, {
                    [style.visible]: inView,
                })}
            >
                <div ref={ref} className={style.clipWrapper}>
                    <div className={style.eventImageContainer}>
                        <img
                            alt={event?.imageFile?.description}
                            src={event?.imageFile?.url}
                        />
                    </div>
                </div>
                <div className={style.date}>
                    {new Date(event.date).getFullYear()}
                </div>
                <div className={style.headline}>{event.headline}</div>
                <div className={style.amount}>{event.name}</div>
                <div
                    className={cx(
                        style.description,
                        open ? style.descriptionOpen : ''
                    )}
                >
                    <ReactMarkdown
                        className={style.descriptionMarkdown}
                        source={event.body}
                    />
                </div>
                <div
                    onClick={() => {
                        setOpen(!open)
                    }}
                    className={cx(style.button, open ? style.buttonClosed : '')}
                >
                    {event.buttonText}
                    <svg
                        className={style.learnMoreIcon}
                        width="11"
                        height="10"
                        viewBox="0 0 11 10"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M10.154 5.888H6.332V9.71H4.408V5.888H0.612V3.99H4.408V0.168H6.332V3.99H10.154V5.888Z"
                            fill="#F67C0F"
                        />
                    </svg>
                </div>
            </div>
        </div>
    )
}

const PastEvents = ({ events }: Props): JSX.Element => {
    const { ref, inView } = useInView({
        threshold: 0.05,
        triggerOnce: true,
    })

    return (
        <div className={style.pastEventsContainer}>
            <div
                style={{ backgroundImage: `url("${events?.imageFile?.url}")` }}
            >
                <div className={style.pastEventsContent}>
                    <div className={style.sectionTitle}>{events.headline}</div>
                    <div ref={ref} className={style.eventsWrapper}>
                        {(events?.contentCollection?.items || []).map(
                            (event, index) => {
                                return (
                                    <Event
                                        key={event.headline}
                                        event={event}
                                        parentInView={inView}
                                        row={Math.floor(index / 2)}
                                    />
                                )
                            }
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default PastEvents
